import React, {CSSProperties, DragEvent, PropsWithChildren, useRef} from "react";

export type DraggedOverPart = "TOP" | "BOTTOM" | "NONE";

interface Props {
  /**
   * The CSS class(es)
   */
  className?: string;

  /**
   * The element-level CSS styling
   */
  style?: CSSProperties;

  /**
   * Called when this Draggable is being dragged over by another Draggable
   * @param dragOverPart the part of this Draggable that the mouse is currently over
   */
  onDragOverPartChange: (dragOverPart: DraggedOverPart) => void;

  /**
   * Called whenever another Draggable is dropped onto this one.
   * @param id The id of the Draggable that is dropped onto this one (that was passed as a prop).
   */
  onDrop: (id: string) => void;

  /**
   * An id to identify this Draggable as a drag target (for example, a layer id)
   */
  id: string;
}

const findDragOverPart = (domNode: HTMLDivElement, e: DragEvent<HTMLDivElement>): DraggedOverPart => {
  if (domNode) {
    const rect = domNode.getBoundingClientRect();
    const middleY = rect.top + (rect.height / 2);
    if (e.clientY < middleY) {
      return "TOP";
    } else {
      return "BOTTOM";
    }
  }
  return "NONE";
}

export const Draggable = ({
                            className,
                            style,
                            onDragOverPartChange,
                            onDrop,
                            id,
                            children
                          }: PropsWithChildren<Props>) => {
  const domNode = useRef<HTMLDivElement>(null);

  // handler for the node being dragged
  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text", id);
  }

  // handler for the node being dragged over
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (domNode.current) {
      const draggedOverPart = findDragOverPart(domNode.current, e);
      onDragOverPartChange(draggedOverPart);
    }
  }

  // handler for the node being dragged over
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Necessary. Allows us to drop.
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move'; //change to none for default cursor
    if (domNode.current) {
      const draggedOverPart = findDragOverPart(domNode.current, e);
      onDragOverPartChange(draggedOverPart);
    }
  }

  // handler for the node being dragged over
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onDragOverPartChange("NONE");
  }

  // handler for the node being dragged over
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onDragOverPartChange("NONE");
    const droppedId = e.dataTransfer.getData('text');
    onDrop(droppedId);
  }

  return (
      <div
          ref={domNode}
          className={className}
          style={style}
          draggable={true}

          // handlers for the node being dragged
          onDragStart={handleDragStart}

          // handlers for the node being dragged over
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
      >
        {children}
      </div>
  )
}